// @cogo/template — shared slot-address resolution/write logic
//
// PURE leaf module: no node:fs, no network, no Date.now/Math.random.
// Single source of truth for "resolve a SlotAddress against an AppBundle's
// files map and write a value at it" — consumed by both canonical.ts's
// `maskBundle` (writes deterministic mask tokens, for structure hashing)
// and instantiate.ts's `instantiate` (writes real slot values). Before this
// module existed, the two call sites carried byte-identical copies of this
// logic with no shared source — a drift hazard flagged in review.
//
// Throws TypeError when `address` doesn't resolve (missing file, missing
// element id, missing datasource, non-object app.json, etc.). Callers that
// need a non-throwing, typed-failure result (e.g. instantiate's ghost-
// address handling) must catch and convert themselves — this module stays
// deliberately throw-based so both consumers keep their own error-handling
// policy (maskBundle lets it propagate; instantiate converts it to a
// `slot-address-missing` TypedFailure).

import type { AppBundle, Json, SlotAddress } from './types'

function isPlainObject(value: unknown): value is { [k: string]: Json } {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/** Exhaustiveness guard: fails to compile if a SlotAddress.kind case is left unhandled. */
function assertNever(x: never): never {
  throw new TypeError(`resolve-address: unreachable address kind "${String((x as { kind?: unknown })?.kind)}"`)
}

// ---------------------------------------------------------------------------
// JSON Pointer (RFC 6901) — minimal read/write support for `app`/`record-list`
// slot addresses.
// ---------------------------------------------------------------------------

function unescapePointerToken(token: string): string {
  return token.replace(/~1/g, '/').replace(/~0/g, '~')
}

function splitPointer(pointer: string): string[] {
  if (pointer === '') return []
  if (!pointer.startsWith('/')) {
    throw new TypeError(`resolve-address: invalid JSON pointer "${pointer}" (must start with "/")`)
  }
  return pointer
    .slice(1)
    .split('/')
    .map(unescapePointerToken)
}

function setAtPointer(root: Json, pointer: string, value: Json): void {
  const tokens = splitPointer(pointer)
  if (tokens.length === 0) {
    throw new TypeError('resolve-address: cannot set document root via empty JSON pointer')
  }
  let cur: any = root
  for (let i = 0; i < tokens.length - 1; i++) {
    if (cur === null || typeof cur !== 'object') {
      throw new TypeError(`resolve-address: JSON pointer "${pointer}" does not resolve`)
    }
    cur = Array.isArray(cur) ? cur[Number(tokens[i])] : cur[tokens[i]]
  }
  if (cur === null || typeof cur !== 'object') {
    throw new TypeError(`resolve-address: JSON pointer "${pointer}" does not resolve`)
  }
  const lastToken = tokens[tokens.length - 1]
  if (Array.isArray(cur)) {
    cur[Number(lastToken)] = value
  } else {
    cur[lastToken] = value
  }
}

/** Read counterpart of `setAtPointer` — walks the same tokens, returns the value instead of writing it. */
function getAtPointer(root: Json, pointer: string): Json {
  const tokens = splitPointer(pointer)
  let cur: any = root
  for (const token of tokens) {
    if (cur === null || typeof cur !== 'object') {
      throw new TypeError(`resolve-address: JSON pointer "${pointer}" does not resolve`)
    }
    cur = Array.isArray(cur) ? cur[Number(token)] : cur[token]
  }
  return cur as Json
}

// ---------------------------------------------------------------------------
// Element tree lookup — first preorder node whose `id` matches.
// ---------------------------------------------------------------------------

function findElementNode(tree: unknown, elementId: string): { [k: string]: Json } | undefined {
  if (tree === null || typeof tree !== 'object') return undefined
  if (Array.isArray(tree)) {
    for (const item of tree) {
      const found = findElementNode(item, elementId)
      if (found) return found
    }
    return undefined
  }
  const obj = tree as { [k: string]: unknown }
  if (typeof obj.id === 'string' && obj.id === elementId) {
    return obj as { [k: string]: Json }
  }
  for (const key of Object.keys(obj)) {
    const found = findElementNode(obj[key], elementId)
    if (found) return found
  }
  return undefined
}

// ---------------------------------------------------------------------------
// Shared resolution walk — resolves `address` against `files` to a
// {get, set} container pointing at the exact slot both `applyAtAddress`
// (write) and `readAtAddress` (read) operate on. Throws `TypeError` when the
// address does not resolve: missing block ui.json/data.json, no element with
// a matching `id`, missing "app.json", a non-object "app.json" for a
// datasource address, no datasources object map, no datasource with a
// matching key, or a record-list `file` that isn't present in `files`. Both
// public functions below are thin wrappers over this walk so the resolution
// logic — and its exact throw messages — has a single source of truth.
// ---------------------------------------------------------------------------

interface AddressContainer {
  get(): Json
  set(value: Json): void
}

function resolveContainer(files: AppBundle['files'], address: SlotAddress): AddressContainer {
  switch (address.kind) {
    case 'element': {
      const path = `blocks/${address.blockId}/ui.json`
      const tree = files[path]
      if (tree === undefined) {
        throw new TypeError(`resolve-address: missing block ui.json at "${path}"`)
      }
      const node = findElementNode(tree, address.elementId)
      if (!node) {
        throw new TypeError(`resolve-address: element "${address.elementId}" not found in "${path}"`)
      }
      return {
        get: () => node[address.field],
        set: (value: Json) => {
          node[address.field] = value
        },
      }
    }
    case 'app': {
      const appJson = files['app.json']
      if (appJson === undefined) {
        throw new TypeError('resolve-address: missing "app.json"')
      }
      return {
        get: () => getAtPointer(appJson, address.pointer),
        set: (value: Json) => setAtPointer(appJson, address.pointer, value),
      }
    }
    case 'datasource': {
      // `app.json`'s `datasources` is a canonical OBJECT MAP keyed by
      // datasourceId (packages/cogo-schema/v4/app.cogo.schema.json ~L126) —
      // NOT an array of `{id, baseUrl}` records.
      const appJson = files['app.json']
      if (!isPlainObject(appJson)) {
        throw new TypeError('resolve-address: missing/invalid "app.json" for datasource address')
      }
      const datasources = appJson['datasources']
      if (!isPlainObject(datasources)) {
        throw new TypeError('resolve-address: "app.json" has no datasources object map')
      }
      const ds = datasources[address.datasourceId]
      if (!isPlainObject(ds)) {
        throw new TypeError(`resolve-address: datasource "${address.datasourceId}" not found`)
      }
      return {
        get: () => ds[address.field],
        set: (value: Json) => {
          ds[address.field] = value
        },
      }
    }
    case 'record-list': {
      const path = address.file
      const dataJson = files[path]
      if (dataJson === undefined) {
        throw new TypeError(`resolve-address: missing "${path}"`)
      }
      // pointer === "" is RFC 6901's whole-document pointer: `file`'s root
      // value IS the record array (the convention every bundle-level
      // `data/<name>.json` in this repo uses). setAtPointer intentionally
      // refuses to write the document root (see below) — that guard exists
      // for the general JSON-pointer case (e.g. `app` addresses), where
      // replacing the whole file makes no sense. For record-list it is
      // exactly the correct write, so it's handled as its own branch here
      // instead of relaxing setAtPointer's guard for every address kind.
      if (address.pointer === '') {
        return {
          get: () => files[path],
          set: (value: Json) => {
            files[path] = value
          },
        }
      }
      return {
        get: () => getAtPointer(dataJson, address.pointer),
        set: (value: Json) => setAtPointer(dataJson, address.pointer, value),
      }
    }
    default:
      return assertNever(address)
  }
}

/**
 * Resolves `address` against `files` (an `AppBundle["files"]` map) and
 * writes `value` at it, mutating `files` (and the object graph reachable
 * from it) in place. Throws `TypeError` when the address does not resolve —
 * see `resolveContainer` for the exact conditions and messages.
 */
export function applyAtAddress(files: AppBundle['files'], address: SlotAddress, value: Json): void {
  resolveContainer(files, address).set(value)
}

/**
 * Read counterpart of `applyAtAddress` — resolves `address` against `files`
 * via the same walk (element = first preorder `id` match, JSON pointer for
 * `app`/`record-list`, datasource object-map lookup) and returns the CURRENT
 * value there instead of writing. Throws the same `TypeError` message shapes
 * as `applyAtAddress` when the address does not resolve.
 */
export function readAtAddress(files: AppBundle['files'], address: SlotAddress): Json {
  return resolveContainer(files, address).get()
}
